import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { toast } from "sonner"
import { z } from "zod/v4"

import { OrganizationGuard } from "@/components/organization-guard"
import { DeleteOrganizationDialog } from "@/components/settings/delete-organization-dialog"
import { SettingsFormSkeleton } from "@/components/skeletons/settings-form-skeleton"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Form, FormInput } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { useFormState } from "@/hooks/use-form-state"
import { updateOrganization } from "@/http/organizations"
import { useAuth } from "@/store/auth"
import { useOrganization } from "@/store/organization"

const updateOrganizationSchema = z.object({
  name: z.string().min(1, "Nome da organização e obrigatorio"),
})

export const Route = createFileRoute("/_authenticated/$orgSlug/settings/")({
  component: OrganizationSettings,
  pendingComponent: SettingsFormSkeleton,
})

function OrganizationSettings() {
  const { currentOrg, loadOrganizations, setCurrentOrg } = useOrganization()
  const { user } = useAuth()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const { state, ...form } = useFormState({
    zodSchema: updateOrganizationSchema,
    defaultValues: {
      name: currentOrg?.name,
    },
    action: async (data) => {
      const response = await updateOrganization(
        currentOrg?.slug ?? "",
        data.name,
      )

      if (response.error) {
        return {
          success: false,
          message: response.error.message || "Erro ao atualizar organização",
        }
      }

      return {
        success: true,
        message: "Organização atualizada com sucesso",
        params: { organization: response.data.organization },
      }
    },
    onSuccess: async (_, params) => {
      if (params?.organization) {
        setCurrentOrg({
          ...currentOrg,
          ...params.organization,
        })
      }
      await loadOrganizations()
      toast.success("Organização atualizada com sucesso!")
    },
    onFailure: (_, error) => {
      toast.error(error || "Erro ao atualizar organização")
    },
  })

  if (!currentOrg) {
    return null
  }

  const isUnchanged = form.watch("name") === currentOrg.name
  const isOwner = user?.id === currentOrg.ownerId

  return (
    <div className="space-y-6">
      <OrganizationGuard
        action="update"
        subject="Organization"
        fallback={
          <Card>
            <CardHeader>
              <CardTitle>Organização</CardTitle>
              <CardDescription>
                Você não tem permissão para editar os dados da organização.
              </CardDescription>
            </CardHeader>
          </Card>
        }
      >
        <Card>
          <CardHeader>
            <CardTitle>Organização</CardTitle>
            <CardDescription>
              Atualize o nome exibido da organização.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <div className="space-y-4">
                <FormInput name="name" label="Nome da organização">
                  <Input placeholder="Nome da organização" />
                </FormInput>
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={state.isSubmitting || isUnchanged}
                  >
                    {state.isSubmitting ? "Salvando..." : "Salvar"}
                  </Button>
                </div>
              </div>
            </Form>
          </CardContent>
        </Card>
      </OrganizationGuard>

      {isOwner && (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">Zona de perigo</CardTitle>
            <CardDescription>
              Ações irreversíveis para esta organização.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Excluir organização</p>
                <p className="text-muted-foreground text-sm">
                  Remove permanentemente a organização e todos os seus dados.
                </p>
              </div>
              <Button
                type="button"
                variant="destructive"
                onClick={() => setDeleteDialogOpen(true)}
              >
                Excluir
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <DeleteOrganizationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        organizationSlug={currentOrg.slug}
        organizationName={currentOrg.name}
      />
    </div>
  )
}
